import AgoraRTC from 'agora-rtc-react';
import type { IAgoraRTCClient, IMicrophoneAudioTrack, ICameraVideoTrack } from 'agora-rtc-react';
import { StudioLogger } from '@studio/logger';
import { setupEventListeners } from '../events/setupEventListeners';
import { setupDeviceListeners, setupPlaybackDeviceListener } from '../events/setupDeviceListeners';
import { BACKGROUND_OPTIONS, getVFXConfig, transformVideo } from '../virtualBackgrounds/virtualBackground';
import { NetworkHealthDispatcher } from '../metrics/NetworkHealthDispatcher';
import { AGORA_ENCODER_CONFIG } from '@studio/constants';
const LOG = new StudioLogger('joinAgoraMeeting');

/* eslint-disable no-unused-vars */
interface Actions {
  showModal?: (...args: any[]) => any;
  showNotification: (...args: any[]) => any;
  clearNotification?: (...args: any[]) => any;
  handleVideoInputChanged: (...args: any[]) => any;
  handleAudioInputChanged: (...args: any[]) => any;
  setDesiredAttendeeCameraStatus?: (...args: any[]) => any;
  addToAttendeeTiles: (...args: any[]) => any;
  setAttendeeCameraStatus: (...args: any[]) => any;
  setSourceInitializing: (...args: any[]) => any;
  setAttendeeMuteStatus: (...args: any[]) => any;
  setAttendeeVoiceIntensity: (...args: any[]) => any;
  setAttendeePresence: (...args: any[]) => any;
  disconnectSource: (...args: any[]) => any;
  setAttendeeNetworkHealth?: (...args: any[]) => any;
  setSourceAsUnassigned?: (...args: any[]) => any;
  updateSource?: (...args: any[]) => any;
}

interface JoinMeetingParams {
  channel: string;
  token: string;
  appId: string;
  audioInputDevice: string;
  videoInputDevice: string;
  attendeeId: string;
  actions: Actions;
  userBackground: BACKGROUND_OPTIONS;
  dispatch: (action: any) => void;
}

/**
 * Joins an Agora RTC meeting with the specified parameters
 *
 * @param {JoinMeetingParams} params - The parameters for joining the meeting
 * @param {string} params.channel - The channel name to join
 * @param {string} params.token - The token for authentication
 * @param {string} params.appId - The Agora application ID
 * @param {string} params.audioInputDevice - The selected audio input device ID
 * @param {string} params.videoInputDevice - The selected video input device ID
 * @param {string} params.attendeeId - The unique ID for the attendee joining the meeting
 * @param {Actions} params.actions - Actions that can be dispatched during the meeting
 * @param {BACKGROUND_OPTIONS} params.userBackground - The virtual background option selected by the user
 * @param {Function} params.dispatch - Redux dispatch function
 *
 * @returns {Promise<{localAudioTrack: IMicrophoneAudioTrack, localVideoTrack: ICameraVideoTrack, client: IAgoraRTCClient}>}
 *          A promise that resolves to the created audio track, video track, and client
 */
export const joinMeeting = async ({
  channel,
  token,
  appId,
  audioInputDevice,
  videoInputDevice,
  attendeeId,
  actions,
  userBackground,
  dispatch
}: JoinMeetingParams): Promise<{
  localAudioTrack: IMicrophoneAudioTrack | null;
  localVideoTrack: ICameraVideoTrack | null;
  client: IAgoraRTCClient;
}> => {
  const client = AgoraRTC.createClient({ mode: 'rtc', codec: 'vp9' });

  // Default is 2 seconds which leads to some noticeable lag in volume indicator updates
  // @ts-ignore - setParameter exists in Agora SDK runtime but is not included in TypeScript definitions
  AgoraRTC.setParameter('AUDIO_VOLUME_INDICATION_INTERVAL', 200);

  setupEventListeners(client, dispatch, actions);

  NetworkHealthDispatcher.getInstance(client, attendeeId, dispatch, actions.setAttendeeNetworkHealth);

  LOG.info('Attempting to join Agora channel', {
    channel,
    attendeeId,
    appId
  });

  await client.join(appId, channel, token, attendeeId);

  LOG.info('Successfully joined Agora channel', {
    channel,
    attendeeId
  });

  await client.enableDualStream();
  LOG.info(`Successfully dual stream mode enabled for Agora channel: ${channel}`);

  client.setLowStreamParameter({
    width: 320,
    height: 240,
    framerate: 15,
    bitrate: 400
  });

  const localAudioTrack: IMicrophoneAudioTrack = await AgoraRTC.createMicrophoneAudioTrack({
    microphoneId: audioInputDevice,
    ANS: true,
    AEC: true
  });

  let localVideoTrack: ICameraVideoTrack | null = null;
  try {
    localVideoTrack = await AgoraRTC.createCameraVideoTrack({
      cameraId: videoInputDevice,
      encoderConfig: AGORA_ENCODER_CONFIG
    });
  } catch (error: any) {
    if (error.code === 'NOT_READABLE') {
      LOG.error('Camera in use by another application', error);
    } else {
      LOG.error('Error creating camera video track', error);
    }
  }

  const deviceActions = {
    handleVideoInputChanged: actions.handleVideoInputChanged,
    handleAudioInputChanged: actions.handleAudioInputChanged,
    showNotification: actions.showNotification,
    addToAttendeeTiles: actions.addToAttendeeTiles
  };

  setupDeviceListeners(localAudioTrack, localVideoTrack, dispatch, deviceActions, attendeeId);
  setupPlaybackDeviceListener();

  const tracksToPublish = localVideoTrack ? [localAudioTrack, localVideoTrack] : [localAudioTrack];
  await client.publish(tracksToPublish);

  LOG.info(`Successfully joined Agora channel: ${channel} as ${attendeeId}`);

  if (localVideoTrack && userBackground !== BACKGROUND_OPTIONS.NONE) {
    const videoFxConfig = getVFXConfig(userBackground);
    if (videoFxConfig) {
      await transformVideo(localVideoTrack, videoFxConfig, videoInputDevice);
    }
  }
  return {
    localAudioTrack,
    localVideoTrack,
    client
  };
};
